import {
    ReleaseStepType,
    TimelineEventType,
    TimelineEvent,
    TimelineEventReleaseStepStatusChangedData,
    TimelineEventReleaseStepChecklistCompletedData,
    TimelineEventChecklistItemStatusUpdatedData,
    TimelineEventChecklistItemCommentUpdatedData,
    TimelineEventInitiator,
} from '../types';
import { allCases } from '../typescriptHelpers';
import { formatDateString } from '../helpers/displayHelpers';
import { reduce, Dictionary, keys } from 'lodash';
import { dayjs } from '@/extendedDayjs';
import { TIMELINE_EVENT_TYPES_BY_RELEASE_STEP_TYPE } from '../components/timelineEvents/timelineEventHelpers';

export enum TimelineEventsFilterType {
    Date = 'date',
    ReleaseStep = 'releaseStep',
    Automation = 'automation',
}

export const AllTimelineEventsFilterTypes = allCases(TimelineEventsFilterType);

export interface TimelineEventsFilter {
    type: TimelineEventsFilterType;
}

export interface DateFilter extends TimelineEventsFilter {
    type: TimelineEventsFilterType.Date;
    date: string[];
}

export interface ReleaseStepFilter extends TimelineEventsFilter {
    type: TimelineEventsFilterType.ReleaseStep;
    releaseStep: ReleaseStepType;
}

export interface AutomationFilter extends TimelineEventsFilter {
    type: TimelineEventsFilterType.Automation;
    automation: TimelineEventType;
}

export type AnyTimelineEventsFilter = DateFilter | ReleaseStepFilter | AutomationFilter;

export const shortDescriptionForTimelineEventFilter: (filter: TimelineEventsFilter) => string = (filter) => {
    switch (filter.type) {
        case TimelineEventsFilterType.Date: {
            const dateFilter = filter as DateFilter;
            return `Timeline events between ${formatDateString(dateFilter.date[0], 'dddd, MMM D, YYYY')} and ${formatDateString(dateFilter.date[1], 'dddd, MMM D, YYYY')}`;
        }
        case TimelineEventsFilterType.ReleaseStep: {
            const releaseStepFilter = filter as ReleaseStepFilter;
            return `Release step: ${releaseStepFilter.releaseStep}`;
        }
        case TimelineEventsFilterType.Automation: {
            const automationFilter = filter as AutomationFilter;
            return `Automation: ${automationFilter.automation}`;
        }
    }
};

export const filterTimelineEvent: (
    event: TimelineEvent,
    filtersMap: Dictionary<AnyTimelineEventsFilter[]>,
    shouldShowAutomationsTimelineEventsOnly: boolean,
) => boolean = (event, filtersMap, shouldShowAutomationsTimelineEventsOnly) => {
    const includeEvent = reduce(
        keys(filtersMap),
        (includeEvent, filterType) => {
            if (includeEvent) {
                switch (filterType) {
                    case TimelineEventsFilterType.Date: {
                        const filter = filtersMap[filterType][0] as DateFilter;
                        const filterStartDate = dayjs(filter.date[0]);
                        const filterEndDate = dayjs(filter.date[1]);
                        const eventDate = dayjs(event.triggeredAt);
                        return eventDate.isBetween(filterStartDate, filterEndDate, 'day', '[]');
                    }
                    case TimelineEventsFilterType.ReleaseStep: {
                        const filters = filtersMap[filterType];
                        const allReleaseSteps = filters.map((filter) => (filter as ReleaseStepFilter).releaseStep);

                        for (const releaseStep of allReleaseSteps) {
                            switch (event.type) {
                                case TimelineEventType.ReleaseStepStatusChanged:
                                case TimelineEventType.ReleaseStepChecklistCompleted:
                                case TimelineEventType.ChecklistItemStatusUpdated:
                                case TimelineEventType.ChecklistItemCommentUpdated: {
                                    const data = event.eventData as TimelineEventReleaseStepStatusChangedData &
                                        TimelineEventReleaseStepChecklistCompletedData &
                                        TimelineEventChecklistItemStatusUpdatedData &
                                        TimelineEventChecklistItemCommentUpdatedData;

                                    return (data?.checklistItemPlacement ?? data?.releaseStepType) === releaseStep;
                                }
                                default:
                                    break;
                            }
                            return TIMELINE_EVENT_TYPES_BY_RELEASE_STEP_TYPE[releaseStep].includes(event.type);
                        }

                        return true;
                    }
                    case TimelineEventsFilterType.Automation: {
                        const filters = filtersMap[filterType];
                        const allAutomations = filters.map((filter) => (filter as AutomationFilter).automation);
                        return (
                            allAutomations.includes(event.type) && event.triggeredBy === TimelineEventInitiator.Runway
                        );
                    }
                    default:
                        return true;
                }
            } else {
                return includeEvent;
            }
        },
        true,
    );

    if (shouldShowAutomationsTimelineEventsOnly) {
        return includeEvent && event.triggeredBy === TimelineEventInitiator.Runway;
    }
    return includeEvent;
};
